<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-alert type="info" text>
        CLDY retail customers listed here cannot receive domain promotion discounts.
        Temporary access lasts until the next daily eligibility scan and never changes
        hosting, coupon, or referral discounts.
      </v-alert>

      <v-card class="mb-4" outlined>
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="8">
              <v-text-field
                v-model="searchTerm"
                label="Search customer ID, email, name, phone, reason, or source ID"
                clearable
                hide-details
                @keyup.enter="search"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4" class="text-md-right">
              <v-btn color="primary" class="mr-2" @click="search">Search</v-btn>
              <v-btn text @click="resetSearch">Reset</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loading"
        class="elevation-1"
        :footer-props="{
          'items-per-page-options': [30, 60, 100, 150, 250],
        }"
      >
        <template v-slot:item.customer="{ item }">
          <div>{{ item.first_name }} {{ item.last_name }}</div>
          <small>{{ item.email }}</small>
        </template>

        <template v-slot:item.reason="{ item }">
          <v-chip x-small :color="reasonColor(item.reason)">
            {{ reasonLabel(item.reason) }}
          </v-chip>
        </template>

        <template v-slot:item.source="{ item }">
          {{ item.source_connection }} #{{ item.source_user_id }}
        </template>

        <template v-slot:item.temporary_eligible="{ item }">
          <v-chip x-small :color="Number(item.temporary_eligible) === 1 ? 'success' : 'error'">
            {{ Number(item.temporary_eligible) === 1 ? 'Temporarily eligible' : 'Blocked' }}
          </v-chip>
          <div v-if="Number(item.temporary_eligible) === 1" class="caption mt-1">
            Until {{ item.override_until }} UTC
          </div>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn
            v-if="Number(item.temporary_eligible) !== 1"
            x-small
            color="success"
            :loading="actionUserId === Number(item.user_id)"
            :disabled="actionUserId !== null"
            @click="setTemporaryEligibility(item, true)"
          >
            Allow until next scan
          </v-btn>
          <v-btn
            v-else
            x-small
            color="error"
            :loading="actionUserId === Number(item.user_id)"
            :disabled="actionUserId !== null"
            @click="setTemporaryEligibility(item, false)"
          >
            End temporary access
          </v-btn>
        </template>
      </v-data-table>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: 'CldyDomainPromotionEligibility',
  data() {
    return {
      items: [],
      totalItems: 0,
      loading: false,
      actionUserId: null,
      searchTerm: '',
      appliedSearchTerm: '',
      options: {
        page: 1,
        itemsPerPage: 30,
        sortBy: ['user_id'],
        sortDesc: [true],
      },
      headers: [
        { text: 'User ID', value: 'user_id' },
        { text: 'Customer', value: 'customer', sortable: false },
        { text: 'Phone', value: 'phone_number', sortable: false },
        { text: 'Reason', value: 'reason' },
        { text: 'Source', value: 'source', sortable: false },
        { text: 'First Domain', value: 'first_domain_at' },
        { text: 'Detected', value: 'detected_at' },
        { text: 'Last Scan', value: 'last_evaluated_at' },
        { text: 'Promotion Status', value: 'temporary_eligible', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
    };
  },
  watch: {
    options: {
      handler() {
        this.loadTable();
      },
      deep: true,
    },
  },
  mounted() {
    this.loadTable();
  },
  methods: {
    loadTable() {
      const {
        sortBy, sortDesc, page, itemsPerPage,
      } = this.options;
      const orderColumn = sortBy.length > 0 ? sortBy[0] : 'user_id';
      const descending = sortDesc.length > 0 ? sortDesc[0] : true;
      this.loading = true;

      return this.$api.post('admin/cldy-domain-promotion-eligibility/list', {
        search: { '*': this.appliedSearchTerm },
        current_page: page,
        per_page: itemsPerPage,
        order: `${orderColumn}:${descending}`,
      }).then((response) => {
        this.items = response.data.data;
        this.totalItems = response.data.total;
      }).catch((error) => {
        this.showError(error);
      }).finally(() => {
        this.loading = false;
      });
    },
    search() {
      this.appliedSearchTerm = String(this.searchTerm || '').trim();
      if (this.options.page !== 1) {
        this.options.page = 1;
      } else {
        this.loadTable();
      }
    },
    resetSearch() {
      this.searchTerm = '';
      this.appliedSearchTerm = '';
      if (this.options.page !== 1) {
        this.options.page = 1;
      } else {
        this.loadTable();
      }
    },
    setTemporaryEligibility(item, temporaryEligible) {
      this.actionUserId = Number(item.user_id);
      this.$api.put(`admin/cldy-domain-promotion-eligibility/${item.user_id}`, {
        temporary_eligible: temporaryEligible,
      }).then((response) => {
        this.$store.dispatch('addAlert', {
          id: Date.now(),
          type: 'success',
          message: response.data.message,
        });
        return this.loadTable();
      }).catch((error) => {
        this.showError(error);
      }).finally(() => {
        this.actionUserId = null;
      });
    },
    reasonLabel(reason) {
      return reason === 'matching_phone'
        ? 'Matching phone'
        : 'Domain older than three months, no hosting';
    },
    reasonColor(reason) {
      return reason === 'matching_phone' ? 'warning' : 'error';
    },
    showError(error) {
      const response = error.response || {};
      const data = response.data || {};
      this.$store.dispatch('addAlert', {
        id: Date.now(),
        type: 'error',
        message: data.message || 'Unable to update CLDY domain-promotion eligibility.',
      });
    },
  },
};
</script>

<style scoped>
</style>
